import { Component } from '@nicolas-riciotti/light2'
import Tween from '../helpers/Tween.js';
import Lenis from '../helpers/Lenis'
import getPosition from '../helpers/getPosition.js';

export default class AppHeader extends Component {

  created() {
    this.siteSettingsMotionChange = this.siteSettingsMotionChange.bind(this);
    this.onKeydown = this.onKeydown.bind(this)
    this.onScroll = this.onScroll.bind(this)
    this.onDocumentClick = this.onDocumentClick.bind(this)
    this.lastScroll = 0;
    this.maxOffset = 0;
    this.offset = 0;
  }

  siteSettingsMotionChange(event) {
    this.stopped = ('off' === event.detail);
  }
  
  onScroll(scroll) {
    // Only hover+pointer (desktop) devices get the hide-on-scroll transform.
    // On touch (iPad/phone) Safari's URL bar slides in/out on scroll, shifting
    // the visual viewport; combined with this translateY transform it leaves the
    // fixed nav clipped ("half cut"). Keep the nav static there.
    const isPointerDevice = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (window.innerWidth > 600 && isPointerDevice) {
      const current = scroll;
      if (current <= 0) {
        this.offset = 0;
        this.lastScroll = 0;
        this.style.transform = 'translateY(0px)';
        return;
      }
      const delta = current - this.lastScroll;
      this.offset += delta;
      this.offset = Math.min(Math.max(this.offset, 0), this.maxOffset);
      this.style.transform = `translateY(${-this.offset}px)`;
      this.lastScroll = current;
    }
    else {
      this.style.transform = `none`;
    }
  }

  onDocumentClick(e) {
    if (!this.contains(e.target)) {
      // Click happened outside this Web Component
      if (this.isMenuOpened) this.closeMenu()
      // if (this.isSearchOpened) this.closeSearch()
    }
  }

  attached() {

    document.addEventListener('site_settings_motion', this.siteSettingsMotionChange, false);

    this.isMenuOpened = false;
    this.isSearchOpened = false;

    this.$menu = this.querySelector('[menu-panel]');
    this.$search = this.querySelector('[search-panel]');
    
    this.$menuBtn = this.querySelectorAll('[menu-button]');
    this.$searchBtn = this.querySelectorAll('[search-button]');

    this.$menuItems = this.querySelectorAll('[menu-item]');

    document.addEventListener('click', this.onDocumentClick, true)

    this.$mainNav = this.querySelector('[main-nav]');
    this.$header = this.querySelector('[main-nav] header');
    this.maxOffset = this.$header.clientHeight + getPosition(this.$header).top;



    
    const mobileQuery = window.matchMedia('(max-width: 600px)');
    // Only hover-capable devices (mouse) get the hover-intent flyout. On touch
    // (iPad/tablet >600px) `pointerenter` would open the flyout but no reliable
    // `pointerleave` fires, so it sticks open and covers the page — those
    // devices use the click-to-toggle handler below instead.
    const hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
    const OPEN_CLASS = 'is-metanav-opened';
    // Desktop flyout intent timing
    const METANAV_OPEN_DELAY = 90;    // settle-to-open: first open waits this long
    const METANAV_CLOSE_DELAY = 220;  // hover-intent: leaving waits this long before closing

    this.$menuItems.forEach((el) => {

      const button = el.querySelector('[menu-item-button]');
      const panel = el.querySelector('[menu-item-panel]');
      if (!button || !panel) return;

      const closeOtherItems = (currentItem) => {
        this.$menuItems.forEach((item) => {
          if (item !== currentItem) {
            setPanel(item, false);
          }
        });
      };

      const setPanel = (item, open) => {
        const itemButton = item.querySelector('[menu-item-button]');
        const itemPanel = item.querySelector('[menu-item-panel]');
        if (!itemPanel) return;

        // Cancel any previous transition-end handler
        if (itemPanel._accordionTransitionEnd) {
          itemPanel.removeEventListener('transitionend', itemPanel._accordionTransitionEnd);
          itemPanel._accordionTransitionEnd = null;
        }

        item.classList.toggle(OPEN_CLASS, open);
        item.isExpanded = open;
        itemButton?.setAttribute('aria-expanded', String(open));

        if (mobileQuery.matches) {
          itemPanel.inert = !open;

          if (open) {
            itemPanel.hidden = false;

            // Start from closed height
            itemPanel.style.maxHeight = '0px';

            requestAnimationFrame(() => {
              itemPanel.style.maxHeight = `${itemPanel.scrollHeight}px`;
            });

            itemPanel._accordionTransitionEnd = (event) => {
              if (event.propertyName !== 'max-height') return;
              if (!item.classList.contains(OPEN_CLASS)) return;

              // Keep open, but allow content to resize naturally
              itemPanel.style.maxHeight = 'none';

              itemPanel.removeEventListener('transitionend', itemPanel._accordionTransitionEnd);
              itemPanel._accordionTransitionEnd = null;
            };

            itemPanel.addEventListener('transitionend', itemPanel._accordionTransitionEnd);
          } else {
            // If already hidden, nothing to animate
            if (itemPanel.hidden) return;

            // If currently open with max-height:none, convert it to px first
            itemPanel.style.maxHeight = `${itemPanel.scrollHeight}px`;

            requestAnimationFrame(() => {
              itemPanel.style.maxHeight = '0px';
            });

            itemPanel._accordionTransitionEnd = (event) => {
              if (event.propertyName !== 'max-height') return;
              if (item.classList.contains(OPEN_CLASS)) return;

              itemPanel.hidden = true;

              itemPanel.removeEventListener('transitionend', itemPanel._accordionTransitionEnd);
              itemPanel._accordionTransitionEnd = null;
            };

            itemPanel.addEventListener('transitionend', itemPanel._accordionTransitionEnd);
          }
        } else {
          itemPanel.hidden = false;
          itemPanel.inert = !open;
          itemPanel.style.maxHeight = '';
        }

        if (!open) {
          itemPanel.querySelectorAll('a, button')?.forEach(elem => elem.setAttribute('tabindex',-1));
        }
        else {
          itemPanel.querySelectorAll('a, button')?.forEach(elem => elem.removeAttribute('tabindex'));
        }
      };

      const resetPanel = () => {
        el.classList.remove(OPEN_CLASS);
        el.isExpanded = false;
        button?.setAttribute('aria-expanded', String(el.isExpanded));
        panel.hidden = false;
        panel.inert = false;
        if (!el.isExpanded) {
          panel.querySelectorAll('a, button')?.forEach(elem => elem.setAttribute('tabindex',-1));
        }
      };

      const initPanel = () => {
        if (!mobileQuery.matches) {
          resetPanel();
          return;
        }
        el.classList.remove(OPEN_CLASS);
        el.isExpanded = false;
        button?.setAttribute('aria-expanded', String(el.isExpanded));
        panel.hidden = true;
        panel.inert = true;
        if (!el.isExpanded) {
          panel.querySelectorAll('a, button')?.forEach(elem => elem.setAttribute('tabindex',-1));
        }
      };
      
      button.addEventListener('click', (event) => {
        const shouldOpen = !el.classList.contains(OPEN_CLASS);
        if (mobileQuery.matches) {
          this.$menuItems.forEach((item) => {
            if (item !== el) {
              setPanel(item, false);
            }
          });
          setPanel(el, shouldOpen);
          return;
        }
        event.preventDefault();
        clearTimeout(this._metanavOpenTimer);
        clearTimeout(this._metanavCloseTimer);
        closeOtherItems(el);
        setPanel(el, shouldOpen);
        this._metanavOpen = shouldOpen;
      });

      // Desktop hover with intent:
      //  - settle-to-open: the first open waits METANAV_OPEN_DELAY so a fast
      //    sweep across the nav doesn't fan every menu open. Switching while a
      //    menu is already open stays instant.
      //  - hover-intent close: leaving starts a cancelable delay so a flicker
      //    off the item (or arcing toward the panel) doesn't snap it shut.
      el.addEventListener('pointerenter', () => {
        if (mobileQuery.matches || !hoverQuery.matches) return;
        clearTimeout(this._metanavOpenTimer);
        clearTimeout(this._metanavCloseTimer);
        const open = () => {
          closeOtherItems(el);
          setPanel(el, true);
          this._metanavOpen = true;
        };
        if (this._metanavOpen) {
          open();
        } else {
          this._metanavOpenTimer = setTimeout(open, METANAV_OPEN_DELAY);
        }
      });
      el.addEventListener('pointerleave', () => {
        if (mobileQuery.matches || !hoverQuery.matches) return;
        clearTimeout(this._metanavOpenTimer);
        clearTimeout(this._metanavCloseTimer);
        this._metanavCloseTimer = setTimeout(() => {
          setPanel(el, false);
          this._metanavOpen = false;
        }, METANAV_CLOSE_DELAY);
      });
      initPanel();
      mobileQuery.addEventListener('change', initPanel);
    });
    
 

    this.$menuBtn.forEach( el => {
      el.addEventListener('click' , e => {
        if (!this.isMenuOpened) {
          this.openMenu();
        }
        else {
          this.closeMenu();
        }
      });
    });

    // this.$searchBtn?.forEach( el => {
    //   el.addEventListener('click' , e => {
    //     if (!this.isSearchOpened) {
    //       this.openSearch();
    //     }
    //     else {
    //       this.closeSearch();
    //     }
    //   });
    // });

    this.resize();

    Lenis.on('scroll', e => {
      this.onScroll( e.animatedScroll );
    });

  }

  openMenu() {
    this.closeSearch();
    this.isMenuOpened = true;
    this.classList.add('is-menu-opened');
    document.addEventListener('keydown', this.onKeydown, false);
    this.$menuBtn.forEach( el => {
      el.setAttribute('aria-expanded', 'true');
      el.setAttribute('aria-label', 'Close Menu');
      el.setAttribute('title', 'Close Menu');
    });
    // this.openPanel(this.menuMask);
    this.trap()
  }
  closeMenu() {
    this.isMenuOpened = false;
    this.classList.remove('is-menu-opened');
    document.removeEventListener('keydown', this.onKeydown);
    this.$menuBtn.forEach( el => {
      el.setAttribute('aria-expanded', 'false');
      el.setAttribute('aria-label', 'Open Menu');
      el.setAttribute('title', 'Open Menu');
    });
    // this.closePanel(this.menuMask);
    this.untrap()
  }

  openSearch() {
    this.closeMenu();
    this.isSearchOpened = true;
    this.classList.add('is-search-opened');
    this.$searchBtn.forEach( el => {
      el.setAttribute('aria-expanded', 'true');
      el.setAttribute('aria-label', 'Close Search');
      el.setAttribute('title', 'Close Search');
    });
    // this.openPanel(this.searchMask);
    this.trap()
  }
  closeSearch() {
    this.isSearchOpened = false;
    this.classList.remove('is-search-opened');
    this.$searchBtn.forEach( el => {
      el.setAttribute('aria-expanded', 'false');
      el.setAttribute('aria-label', 'Open Search');
      el.setAttribute('title', 'Open Search');
    });
    // this.closePanel(this.searchMask);
    this.untrap()
  }

  resize() {
    
    this.maxOffset = this.$header.clientHeight + getPosition(this.$header).top;

    if ( this.$menu ) {
      
      this.mainNavW = this.querySelector('[main-nav] header').clientWidth;
      this.mainNavH = this.querySelector('[main-nav] header').clientHeight;

      const $background = document.querySelectorAll('[data-nav-background]');
      const $color = document.querySelectorAll('[data-nav-color]');
      if ($background.length > 0) {
        this.style.setProperty('--nav-background', $background[0].getAttribute('data-nav-background'));
      }
      if ($color.length > 0) {
        this.style.setProperty('--nav-color', $color[0].getAttribute('data-nav-color'));
      }

      const $menuPanel   = this.$menu.querySelector('[inner-panel]');
      this.menuMask = {
        t: 0,
        r: ($menuPanel.clientWidth/2 - this.mainNavW/2),
        b: ($menuPanel.clientHeight - this.mainNavH) ,
        l: ($menuPanel.clientWidth/2 - this.mainNavW/2),
        el: $menuPanel,
        round: 5,
      }
      $menuPanel.style.clipPath = `inset(${this.menuMask.t}px ${this.menuMask.r}px ${this.menuMask.b}px ${this.menuMask.l}px round ${this.menuMask.round}px)`;

      const $searchPanel = this.$search?.querySelector('[inner-panel]');
      if ($searchPanel) {
        this.searchMask = {
          t: 0,
          r: ($searchPanel.clientWidth/2 - this.mainNavW/2),
          b: ($searchPanel.clientHeight - this.mainNavH) ,
          l: ($searchPanel.clientWidth/2 - this.mainNavW/2),
          el: $searchPanel,
          round: 5,
        }
        $searchPanel.style.clipPath = `inset(${this.searchMask.t}px ${this.searchMask.r}px ${this.searchMask.b}px ${this.searchMask.l}px round ${this.searchMask.round}px)`;
      }

    }
  }

  // openPanel(obj) {
  //   this.animateMask(obj, {t:0, r: 0, b: 0, l: 0, round: 8});
  // }
  // closePanel(obj) {
  //   if (!obj?.el) {
  //     return;
  //   }
  //   const w = obj.el.clientWidth;
  //   const h = obj.el.clientHeight;
  //   const end = {
  //     round: 5,
  //     t: 0,
  //     r: (w/2 - this.mainNavW/2),
  //     b: (h   - this.mainNavH) ,
  //     l: (w/2 - this.mainNavW/2),
  //   }
  //   // this.animateMask(obj, end);
  // }
 
  // animateMask(obj, end) {
  //   const start = {
  //     round: obj.round,
  //     t: obj.t,
  //     r: obj.r,
  //     b: obj.b,
  //     l: obj.l,
  //   }
  //   obj.tween?.destroy();
  //   obj.tween = Tween({e:0}, {
  //       to:       {e:1},
  //       easing:   [0.43, 0.195, 0.02, 1],
  //       duration: this.stopped ? 0.01 : 1000,
  //       step: e => {
  //         obj.round = start.round + (end.round - start.round) * e;
  //         obj.t = start.t + (end.t - start.t) * e;
  //         obj.r = start.r + (end.r - start.r) * e;
  //         obj.b = start.b + (end.b - start.b) * e;
  //         obj.l = start.l + (end.l - start.l) * e;
  //         obj.el.style.clipPath = `inset(${obj.t}px ${obj.r}px ${obj.b}px ${obj.l}px round ${obj.round}px)`;
  //       }
  //   });
  // } 


  onKeydown(event) {

    if (event.key === 'Escape' || event.key === 'Esc') {
      // this.isMenuOpened = false;
      // el.setAttribute('aria-expanded', 'false')
      // el.setAttribute('aria-label', 'Open Menu')
      // el.setAttribute('title', 'Open Menu')
       this.closeMenu()
    }
    
    if (!(event.key === 'Tab' || event.keyCode === 9) || !this.isTrapped) {
      console.log('NOT TAB OR NOT TRAPPED');
      return
    }

    this.getFocusableItems();

    if (event.shiftKey) {
      if (document.activeElement === this.$first_item && this.$last_item) {
        this.$last_item.focus();
        event.preventDefault();
      }
    } 
    else {

      console.log('MOBILE_MENU_onKeydown', document.activeElement, this.$last_item);
      if (document.activeElement === this.$last_item && this.$first_item) {
        this.$first_item.focus();
        event.preventDefault();
      }
    }
  }

  getFocusableItems() {
    // Get focus-able items.
    const input_types = [
      'button', 'checkbox', 'date', 'email', 'file', 'number', 'password',
      'radio', 'range', 'search', 'submit', 'tel', 'text', 'a'
    ]
    const inputs = input_types
      .map(type => `input[type="${type}"]:not([disabled])`).join(', ')
    const selector = `a[href],
      button:not([disabled]), textarea:not([disabled]), ${inputs},
      select:not([disabled]), iframe, [tabindex]:not([tabindex="-1"]`
    const focusable = this?.querySelectorAll(selector)
    const visible = Array.from(focusable);//.filter(child => null === child.closest('.hidden'))
    this.$first_item = visible[0]
    this.$last_item = visible[visible.length - 1]
    console.log('focusable', focusable, this.$first_item, this.$last_item);
  }

  trap() {
    this.isTrapped = true
    // Save the original triggering element to be re-focused.
    this.$previousTrigger = (document.activeElement || document.body)
    this.$previousTrigger.blur()
    this.getFocusableItems()
    // setTimeout(()=>{
    //   this.$first_item.focus();
    // }, 100)
  }

  untrap() {
    this.isTrapped = false
    this.$first_item = null
    this.$last_item = null
  }


}

customElements.define('app-header', AppHeader)